import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Phone, Navigation, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const redPin = L.divIcon({
  className: "",
  iconSize: [34, 44], iconAnchor: [17, 42], popupAnchor: [0, -38],
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44"><path d="M17 1C8.2 1 1 8.1 1 16.9 1 28.6 17 43 17 43s16-14.4 16-26.1C33 8.1 25.8 1 17 1z" fill="#E53935" stroke="#B71C1C" stroke-width="1.5"/><circle cx="17" cy="17" r="6" fill="#fff"/></svg>`,
});

const LAYERS = {
  map: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
};

function telHref(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length === 10) return `tel:+91${d}`;
  return `tel:+${d}`;
}

export default function LocationBlock({ property: p }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [layer, setLayer] = useState("map");
  const isOwn = user?.id === p.owner_id;
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}&travelmode=driving`;

  const onCall = (e) => {
    if (p.owner_phone) return; // native tel: link proceeds
    e.preventDefault();
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/property/${p.id}`)}`); return; }
    toast.info("Owner's number unlocks after you send an inquiry or start a chat.");
  };

  return (
    <div className="mt-8" data-testid="location-block">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Location</div>
      <div className="mt-1 text-sm text-[#5C6B7A]" data-testid="location-address">{p.address}{p.pincode ? ` · ${p.pincode}` : ""}</div>

      {!isOwn && (
        <a
          href={p.owner_phone ? telHref(p.owner_phone) : "#"}
          onClick={onCall}
          className="mt-3 w-full h-12 rounded-lg bg-[#2B3A67] hover:bg-[#1f2c52] text-white font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="call-owner-btn"
        >
          {p.owner_phone ? <Phone size={17} /> : <Lock size={15} />} Call Owner
        </a>
      )}

      <div className="mt-3 rounded-lg overflow-hidden border border-[#1A1F2C]/10">
        <div className="flex bg-[#F0EBE1] text-sm" role="tablist" data-testid="map-layer-tabs">
          {["map", "satellite"].map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={layer === k}
              onClick={() => setLayer(k)}
              className={`flex-1 py-2.5 font-medium capitalize transition-colors ${layer === k ? "bg-[#FDFBF7] text-[#C85A32] border-b-2 border-[#C85A32]" : "text-[#5C6B7A] hover:text-[#1A1F2C]"}`}
              data-testid={`map-tab-${k}`}
            >{k}</button>
          ))}
        </div>
        <div className="h-72">
          <MapContainer key={`${p.latitude},${p.longitude}`} center={[p.latitude, p.longitude]} zoom={p.geo_precision === "city" || p.geo_precision === "country" ? 12 : 15} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer key={layer} url={LAYERS[layer].url} attribution={LAYERS[layer].attribution} maxZoom={19} />
            <Marker position={[p.latitude, p.longitude]} icon={redPin}>
              <Popup>{p.title}<br /><span className="text-xs">{p.locality}, {p.city}</span></Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>

      <a
        href={directions}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 w-full h-12 rounded-lg bg-[#C85A32] hover:bg-[#a8482a] text-white font-medium flex items-center justify-center gap-2 transition-colors"
        data-testid="get-directions-btn"
      >
        <Navigation size={17} /> Get Live Directions
      </a>
      {(p.geo_precision === "city" || p.geo_precision === "country") && (
        <div className="mt-2 text-[11px] text-[#5C6B7A]" data-testid="geo-approx-note">Pin shows the approximate area — exact location shared by the owner on contact.</div>
      )}
    </div>
  );
}
